import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser as getUser } from '../../utils/permissionUtils';

const PrivateRoute = ({ children, allowedRoles = [] }) => {
  const location = useLocation();
  
  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token; // Return true if token exists
  };

  // Get current user with permissions from token
  const getCurrentUser = () => {
    return getUser();
  };

  // Check if user has admin or sub-admin role
  const isAdminOrSubAdmin = () => {
    const user = getCurrentUser();
    return user.role === 'admin' || user.role === 'sub-admin';
  };
  
  // Check if user is personnel
  const isPersonnel = () => {
    const user = getCurrentUser();
    return user.role === 'personnel';
  };
  
  // Check if user is super admin
  const isSuperAdmin = () => {
    const user = getCurrentUser();
    return user.role === 'super-admin';
  };
  
  // Check if user has one of the allowed roles
  const hasAllowedRole = () => {
    if (allowedRoles.length === 0) return true; // No role restrictions
    const user = getCurrentUser();
    return allowedRoles.includes(user.role);
  };
  
  // Check if user has permission to access the current route
  const hasRoutePermission = () => {
    // If we're using the allowedRoles prop, use that for permission check
    if (allowedRoles.length > 0) {
      return hasAllowedRole();
    }
    
    const user = getCurrentUser();
    const path = location.pathname;
    
    // Super admin has access to super admin routes
    if (user.role === 'super-admin') {
      // Super admin can access all super admin routes
      if (path.startsWith('/superadmin')) {
        return true;
      }
      // Super admin shouldn't access other routes
      return false;
    }
    
    // Admin has access to all admin routes
    if (user.role === 'admin') {
      // Admin can access all admin routes
      if (path.startsWith('/admin')) {
        return true;
      }
      // Admin shouldn't access personnel routes
      return false;
    }
    
    // For sub-admin, check specific permissions based on the route
    if (user.role === 'sub-admin') {
      // If sub-admin has full admin privileges, grant access to all admin routes
      if (user.fullAdminPrivileges === true) {
        return path.startsWith('/admin');
      }
      
      // If no permissions object, deny access
      if (!user.permissions) {
        return false;
      }
      
      // Dashboard access
      if (path === '/admin' && user.permissions.dashboard?.view) {
        return true;
      }
      
      // Personnel management access
      if (path === '/admin/personnel' && user.permissions.personnel?.view) {
        return true;
      }
      
      // Site management access
      if (path === '/admin/sites' && user.permissions.sites?.view) {
        return true;
      }
      
      // Statistics access
      if (path === '/admin/statistics' && user.permissions.statistics?.view) {
        return true;
      }
      
      // New Clients access
      if (path === '/admin/clients' && user.permissions.newClients?.view) {
        return true;
      }
      
      // Coverage access
      if (path === '/admin/coverage' && user.permissions.coverage?.view) {
        return true;
      }
      
      // Structure Management access
      if (path === '/admin/structure' && user.permissions.structureManagement?.access) {
        return true;
      }
      
      // Incident Reports access
      if (path === '/admin/incident-reports' && user.permissions.incidentReports?.access) {
        return true;
      }
      
      // Special Assignments access
      if (path === '/admin/special-assignments' && user.permissions.specialAssignments?.access) {
        return true;
      }
      
      // Sub-admin management - check if user has subAdmins permission
      if (path === '/admin/subadmins' && user.permissions.subAdmins?.view) {
        return true;
      }
      
      // Sub-admins shouldn't access personnel routes
      return false;
    }
    
    // For personnel, check if they're accessing personnel routes
    if (user.role === 'personnel') {
      // Personnel can only access personnel routes
      return path.startsWith('/personnel');
    }
    
    return false;
  };

  if (!isAuthenticated()) {
    // Not logged in, redirect to login page
    return <Navigate to="/login" />;
  }

  const user = getCurrentUser();
  const path = location.pathname;

  // Check if the user is trying to access a route they shouldn't
  // Check if user is trying to access a route they don't have permission for
  if (!hasAllowedRole()) {
    return redirectToAppropriateRoute(user);
  }
  
  if (path.startsWith('/admin') && !isAdminOrSubAdmin()) {
    // Not an admin or sub-admin trying to access admin routes
    return redirectToAppropriateRoute(user);
  }

  if (path.startsWith('/personnel') && !isPersonnel() && !isAdminOrSubAdmin()) {
    // Not personnel trying to access personnel routes
    return redirectToAppropriateRoute(user);
  }
  
  if (path.startsWith('/superadmin') && !isSuperAdmin()) {
    // Not super admin trying to access super admin routes
    return redirectToAppropriateRoute(user);
  }
  
  if (!hasRoutePermission()) {
    // User doesn't have permission for this route
    return redirectToAppropriateRoute(user);
  }
  
  // Helper function to redirect user to appropriate route based on role
  function redirectToAppropriateRoute(user) {
    if (user.role === 'super-admin') {
      return <Navigate to="/superadmin" />; // Redirect to super admin dashboard
    } else if (user.role === 'admin' || user.role === 'sub-admin') {
      return <Navigate to="/admin" />; // Redirect to admin dashboard
    } else if (user.role === 'personnel') {
      return <Navigate to="/personnel" />; // Redirect to personnel dashboard
    } else {
      return <Navigate to="/login" />;
    }
  }

  // User is authenticated and has proper permissions, render the children
  return children;
};

export default PrivateRoute;
